import React, { createContext, useContext, useState, useEffect } from 'react';
import { reportsService } from '../services/api/reports';

const ReportsContext = createContext();

export const useReports = () => {
  const context = useContext(ReportsContext);
  if (!context) {
    throw new Error('useReports deve ser usado dentro de um ReportsProvider');
  }
  return context;
};

export const ReportsProvider = ({ children }) => {
  const [reports, setReports] = useState({});
  const [savedReports, setSavedReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    category: 'all',
    status: 'all',
    product: 'all',
    affiliate: 'all'
  });

  // Carregar dados de relatórios
  useEffect(() => {
    // Verificar se o usuário está autenticado antes de carregar
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (token && token !== 'null') {
      loadReportsData();
    }
  }, []);

  const loadReportsData = async () => {
    setLoading(true);
    try {
      console.log('🔄 Carregando dados dos relatórios...');
      
      // Verificar se o usuário está autenticado antes de fazer as chamadas
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (!token || token === 'null') {
        console.log('⚠️ Usuário não autenticado - pulando carregamento de relatórios');
        return;
      }
      
      // Carregar dados reais da API
      const [indicators, affiliatePerformance, churnRate, abandonments, refunds, general] = await Promise.all([
        reportsService.getIndicators(),
        reportsService.getAffiliatePerformance(),
        reportsService.getChurnRate(),
        reportsService.getAbandonments(),
        reportsService.getRefunds(),
        reportsService.getGeneralReport()
      ]);

      // Estruturar dados para o frontend
      const reportsData = {
        indicators: indicators.data,
        affiliatePerformance: affiliatePerformance.data,
        churnRate: churnRate.data,
        abandonments: abandonments.data,
        refunds: refunds.data,
        general: general.data
      };

      setReports(reportsData);

    } catch (error) {
      console.error('Erro ao carregar dados dos relatórios:', error);
      
      // Se for erro de autenticação, não mostrar dados mockados
      if (error.response?.status === 401) {
        console.log('⚠️ Erro de autenticação - não carregando dados de relatórios');
        setReports({});
        return;
      }
      
      // Em caso de outros erros, usar dados mockados como fallback
      setReports({
        indicators: {
          sales: { total: 0, revenue: 0, affiliate_commission: 0, platform_commission: 0 },
          products: { total: 0, active: 0, draft: 0 },
          affiliates: { total: 0, active: 0 },
          conversion: { total_visits: 0, conversions: 0, rate: 0 }
        },
        affiliatePerformance: { top_affiliates: [], performance_metrics: {} },
        churnRate: { summary: { churn_rate: 0 }, monthly_retention: [] },
        abandonments: { summary: { overall_abandonment_rate: 0 }, by_product: [] },
        refunds: { summary: { overall_refund_rate: 0 }, by_product: [] },
        general: { sales: { total: 0, revenue: 0 }, products: { total: 0, active: 0 } }
      });
    } finally {
      setLoading(false);
    }
  };

  // Gerar relatório personalizado
  const generateReport = async (reportConfig) => {
    setLoading(true);
    try {
      const { type, filters, format } = reportConfig;
      
      // Simular geração de relatório
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const report = {
        id: Date.now(),
        type,
        filters,
        format,
        generated_at: new Date().toISOString(),
        data: getFilteredData(filters)
      };

      setReports(prev => ({
        ...prev,
        custom: [...(prev.custom || []), report]
      }));

      return report;
    } catch (error) {
      throw new Error('Erro ao gerar relatório: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Salvar relatório
  const saveReport = async (reportConfig) => {
    try {
      const savedReport = {
        id: Date.now(),
        name: reportConfig.name,
        description: reportConfig.description,
        config: reportConfig,
        created_at: new Date().toISOString(),
        last_generated: new Date().toISOString()
      };

      setSavedReports(prev => [...prev, savedReport]);
      return savedReport;
    } catch (error) {
      throw new Error('Erro ao salvar relatório: ' + error.message);
    }
  };

  // Exportar relatório
  const exportReport = async (reportId, format) => {
    setLoading(true);
    try {
      // Simular exportação
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const report = reports.custom?.find(r => r.id === reportId);
      if (!report) {
        throw new Error('Relatório não encontrado');
      }

      // Simular download
      const fileName = `relatorio_${reportId}_${new Date().toISOString().split('T')[0]}.${format}`;
      
      return {
        fileName,
        downloadUrl: `#download-${reportId}`,
        format
      };
    } catch (error) {
      throw new Error('Erro ao exportar relatório: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Obter dados filtrados
  const getFilteredData = (customFilters = {}) => {
    const activeFilters = { ...filters, ...customFilters };
    let filteredData = [];

    // Filtrar por período
    if (activeFilters.dateFrom || activeFilters.dateTo) {
      // Implementar filtro por data se necessário
    }

    // Filtrar por categoria
    if (activeFilters.category && activeFilters.category !== 'all') {
      // Implementar filtro por categoria se necessário
    }

    // Filtrar por status
    if (activeFilters.status && activeFilters.status !== 'all') {
      // Implementar filtro por status se necessário
    }

    return filteredData;
  };

  // Obter métricas calculadas
  const getMetrics = (data = reports.indicators) => {
    if (!data) return {};
    
    return {
      total_revenue: data.sales?.revenue || 0,
      total_orders: data.sales?.total || 0,
      total_commissions: data.sales?.affiliate_commission || 0,
      platform_commission: data.sales?.platform_commission || 0,
      total_products: data.products?.total || 0,
      active_products: data.products?.active || 0,
      total_affiliates: data.affiliates?.total || 0,
      active_affiliates: data.affiliates?.active || 0,
      conversion_rate: data.conversion?.rate || 0
    };
  };

  // Obter dados para gráficos
  const getChartData = (type, filters = {}) => {
    switch (type) {
      case 'sales_by_day':
        // Usar dados de performance de afiliados se disponível
        if (reports.affiliatePerformance?.sales_by_day) {
          return reports.affiliatePerformance.sales_by_day;
        }
        return [];

      case 'sales_by_category':
        // Usar dados de performance de afiliados se disponível
        if (reports.affiliatePerformance?.sales_by_category) {
          return reports.affiliatePerformance.sales_by_category;
        }
        return [];

      case 'sales_by_affiliate':
        // Usar dados de performance de afiliados se disponível
        if (reports.affiliatePerformance?.top_affiliates) {
          return reports.affiliatePerformance.top_affiliates;
        }
        return [];

      case 'monthly_retention':
        // Usar dados de churn rate se disponível
        if (reports.churnRate?.monthly_retention) {
          return reports.churnRate.monthly_retention;
        }
        return [];

      case 'abandonments_by_product':
        // Usar dados de abandonos se disponível
        if (reports.abandonments?.by_product) {
          return reports.abandonments.by_product;
        }
        return [];

      case 'refunds_by_product':
        // Usar dados de estornos se disponível
        if (reports.refunds?.by_product) {
          return reports.refunds.by_product;
        }
        return [];

      default:
        return [];
    }
  };

  // Agendar relatório
  const scheduleReport = async (reportConfig) => {
    try {
      const scheduledReport = {
        id: Date.now(),
        ...reportConfig,
        status: 'scheduled',
        created_at: new Date().toISOString()
      };

      // Em produção, salvar no backend
      console.log('Relatório agendado:', scheduledReport);
      
      return scheduledReport;
    } catch (error) {
      throw new Error('Erro ao agendar relatório: ' + error.message);
    }
  };

  // Obter relatórios salvos
  const getSavedReports = () => {
    return savedReports;
  };

  // Deletar relatório salvo
  const deleteSavedReport = async (reportId) => {
    try {
      setSavedReports(prev => prev.filter(report => report.id !== reportId));
    } catch (error) {
      throw new Error('Erro ao deletar relatório: ' + error.message);
    }
  };

  const value = {
    reports,
    savedReports,
    loading,
    filters,
    setFilters,
    generateReport,
    saveReport,
    exportReport,
    getFilteredData,
    getMetrics,
    getChartData,
    scheduleReport,
    getSavedReports,
    deleteSavedReport
  };

  return (
    <ReportsContext.Provider value={value}>
      {children}
    </ReportsContext.Provider>
  );
};

export default ReportsContext;


















